import React from 'react';
import { Heatmap, Panel, type DateRange, type HeatmapRow } from '../../../ui-kit';
import TableSectionActions, { TableSectionTools } from './TableSectionActions';
import { useTableSection } from '../useTableSection';
import { formatValue } from '../format';
import type { ManualDashboardPayload, RetentionBlockDef, RetentionCell } from '../../../types/manualDashboard';

interface RetentionPanelProps {
  dashboardId?: string;
  /** The grid as the board payload has it, at the board's grouping. */
  block: RetentionBlockDef;
  payload: ManualDashboardPayload;
  range: DateRange;
  filters: Record<string, string[]>;
  refreshNonce: number;
  loading: boolean;
  onGrainChange?: (blockId: string, grain: string | undefined) => void;
}

const AVERAGE = '__all';

/**
 * Each signup group against each week (or month) since it signed up, shaded
 * by the share of the group that came back that period.
 *
 * Read down a column to compare groups at the same age — did families who
 * joined after a change come back more in their second week? — and along a
 * row to see one group fade. The newest groups sit at the top, as cohorts do
 * on every board, under the average across all of them. Weeks a group has
 * not reached yet are blank.
 */
export default function RetentionPanel({
  dashboardId, block, payload, range, filters, refreshNonce, loading, onGrainChange,
}: RetentionPanelProps) {
  const section = useTableSection({
    dashboardId, blockId: block.id, payload, range, filters, refreshNonce, onGrainChange,
    allowedGrains: block.grains,
  });
  const view =
    (section.source.blocks.find((b) => b.type === 'retention' && b.id === block.id) as
      | RetentionBlockDef
      | undefined) ?? block;
  // The grid draws weekly for a daily board and monthly for a quarterly one,
  // so the select shows what is drawn, not the board's grouping.
  const shown = { ...section, grain: view.grain };

  const many = view.noun?.many ?? 'people';
  const unit = view.unit.toLowerCase();
  const pct = (v: number) => formatValue(v, 'pct');

  const cellsOf = (cells: RetentionCell[], size: number, group: string) =>
    cells.map((c) => ({
      key: String(c.k),
      value: c.value,
      title:
        c.value === null
          ? `${group} has not reached ${view.unit} ${c.k} yet.`
          : `${group} · ${view.unit} ${c.k} · ${c.back ?? 0} of ${size.toLocaleString()} ${many} back (${pct(c.value)})`,
    }));

  const rows: HeatmapRow[] = [
    ...(view.average
      ? [{
          key: AVERAGE,
          label: 'All signup groups',
          cells: view.average.cells.map((c) => ({
            key: String(c.k),
            value: c.value,
            title: c.value === null
              ? undefined
              : `All signup groups · ${view.unit} ${c.k} · ${c.back ?? 0} of ${c.of.toLocaleString()} ${many} back (${pct(c.value)})`,
          })),
          summaries: [{ key: 'size', value: view.average.size }],
        }]
      : []),
    ...[...view.rows].reverse().map((r) => ({
      key: r.key,
      label: r.label,
      cells: cellsOf(r.cells, r.size, `Signed up ${r.label}`),
      summaries: [{ key: 'size', value: r.size }],
    })),
  ];

  // The signup period is nearly everyone, so it would set the scale and
  // leave every later period pale. The shading runs to the busiest later one.
  const later = view.rows.flatMap((r) => r.cells.filter((c) => c.k > 0 && c.value !== null).map((c) => c.value as number));
  const max = later.length ? Math.max(...later) : undefined;

  const grid = () => (
    <Heatmap
      ariaLabel={view.title ?? 'Retention grid'}
      rowLabel="Signed up"
      columns={view.columns}
      rows={rows}
      formatValue={pct}
      max={max}
      summaryColumns={[{ key: 'size', label: many.charAt(0).toUpperCase() + many.slice(1), format: (v) => formatValue(v, 'int') }]}
      cellEmptyText=""
      emptyText={`No ${many} signed up in this range.`}
    />
  );

  return (
    <Panel
      title={block.title}
      subtitle={view.subtitle ?? `The share of each signup group back in each ${unit} after signing up.`}
      info={view.info ?? undefined}
      tools={<TableSectionTools section={shown} />}
      actions={<TableSectionActions section={shown} disabled={loading} />}
      loading={loading || section.regrouping}
      loadingHeight={360}
      footer={section.problem}
      expanded={{ content: grid() }}
    >
      {grid()}
    </Panel>
  );
}
